import { Box, Flex, Skeleton as RadixSkeleton, Text } from "@radix-ui/themes";
import type { SkeletonProps as RadixSkeletonProps } from "@radix-ui/themes";

// Re-export so the codebase can `import { Skeleton } from "@/components/ui/Skeleton"`
// without each caller reaching into Radix Themes directly.
export const Skeleton = RadixSkeleton;
export type SkeletonProps = RadixSkeletonProps;

interface SkeletonTextProps {
  lines?: number;
  // Width per line as a CSS length. Defaults to varying widths so the placeholder
  // doesn't look like a wall of identical bars.
  widths?: string[];
}

export function SkeletonText({ lines = 3, widths }: SkeletonTextProps) {
  const defaultWidths = ["100%", "92%", "78%", "85%", "65%"];
  return (
    <Flex direction="column" gap="2">
      {Array.from({ length: lines }).map((_, i) => (
        <RadixSkeleton key={i} loading width={widths?.[i] ?? defaultWidths[i % defaultWidths.length]} height="14px" />
      ))}
    </Flex>
  );
}

interface SkeletonTableRowsProps {
  rows?: number;
  columns?: number;
}

export function SkeletonTableRows({ rows = 5, columns = 4 }: SkeletonTableRowsProps) {
  return (
    <Flex direction="column" gap="2">
      {Array.from({ length: rows }).map((_, r) => (
        <Flex key={r} gap="3">
          {Array.from({ length: columns }).map((_, c) => (
            <Box key={c} style={{ flex: 1 }}>
              <RadixSkeleton loading height="20px" />
            </Box>
          ))}
        </Flex>
      ))}
    </Flex>
  );
}

interface SkeletonListProps {
  rows?: number;
  // When true, prepends a circular avatar placeholder to each row.
  avatar?: boolean;
}

export function SkeletonList({ rows = 5, avatar = false }: SkeletonListProps) {
  return (
    <Flex direction="column" gap="3">
      {Array.from({ length: rows }).map((_, i) => (
        <Flex key={i} gap="3" align="center">
          {avatar && (
            <RadixSkeleton loading width="36px" height="36px" style={{ borderRadius: "50%" }} />
          )}
          <Flex direction="column" gap="1" style={{ flex: 1 }}>
            <RadixSkeleton loading width="60%" height="14px" />
            <RadixSkeleton loading width="40%" height="12px" />
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
}

export function SkeletonWidget({ height = "100%" }: { height?: string | number }) {
  return (
    <Box p="4" style={{ height, display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <RadixSkeleton loading width="40%" height="12px" />
      <Box style={{ flex: 1 }}>
        <RadixSkeleton loading width="100%" height="100%" />
      </Box>
    </Box>
  );
}

export interface SkeletonTileLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface SkeletonDashboardProps {
  // When provided, renders tiles at these exact grid positions — usually the
  // most recently seen layout for the same dashboard so the skeleton matches
  // the shape of what's loading.
  layout?: SkeletonTileLayout[];
  // Fallback tile count when no layout is provided.
  tiles?: number;
  // Column count for the grid. Matches react-grid-layout's 12-col default.
  cols?: 6 | 12;
  // Pixel height per grid row, matched to Dashboard.tsx's ROW_HEIGHT.
  rowHeight?: number;
}

export function SkeletonDashboard({ layout, tiles = 6, cols = 12, rowHeight = 90 }: SkeletonDashboardProps) {
  const tilesToRender: SkeletonTileLayout[] = layout && layout.length > 0
    ? layout
    : Array.from({ length: tiles }).map((_, i) => ({
        x: (i * 4) % cols,
        y: Math.floor((i * 4) / cols) * 3,
        w: 4,
        h: 3,
      }));
  return (
    <Box style={{ width: "100%", padding: "var(--space-2)" }}>
      <Box style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gridAutoRows: `${rowHeight}px`, gap: "12px" }}>
        {tilesToRender.map((tile, i) => (
          <Box
            key={i}
            style={{
              gridColumn: `${tile.x + 1} / span ${tile.w}`,
              gridRow: `${tile.y + 1} / span ${tile.h}`,
              border: "1px solid var(--gray-5)",
              borderRadius: "var(--radius-3)",
              backgroundColor: "var(--color-panel-solid)",
            }}
          >
            <SkeletonWidget />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

interface SkeletonPageProps {
  // Optional title to show greyed-out above the body so the page doesn't appear blank.
  title?: string;
}

export function SkeletonPage({ title }: SkeletonPageProps) {
  return (
    <Box p="4" style={{ width: "100%" }}>
      {title && (
        <Text size="6" weight="medium" color="gray" style={{ display: "block", marginBottom: "var(--space-4)" }}>
          {title}
        </Text>
      )}
      <RadixSkeleton loading width="100%" height="60px" />
      <Box mt="4">
        <SkeletonTableRows rows={5} columns={4} />
      </Box>
    </Box>
  );
}
